/**
 * Constants for the Rugby Event Tracker
 */

/**
 * Types of events that can be recorded
 */
export const EVENT_TYPES = [
  'Conversion',
  'Drop Goal',
  'Forward Pass',
  'Knock-on',
  'Lineout',
  'Maul',
  'Penalty',
  'Penalty Goal',
  'Penalty Try',
  'Red Card',
  'Ruck',
  'Scrum',
  'Tackle',
  'Try',
  'Turnover',
  'Yellow Card'
];

/**
 * Scoring events and their point values
 */
export const SCORE_EVENTS = {
  'Try': 5,
  'Penalty Try': 7, // Includes automatic conversion
  'Conversion': 2,
  'Penalty Goal': 3,
  'Drop Goal': 3
};

/**
 * Match periods
 */
export const MATCH_PERIODS = [
  'First Half',
  'Second Half',
  'Overtime'
];


/**
 * Default team colors
 */
export const TEAM_COLORS = {
  home: '#604734', // Brown
  away: '#e53935', // Red
};

/**
 * Field dimensions in meters (standard rugby union field)
 */
export const FIELD_DIMENSIONS = {
  width: 70, // Maximum width
  length: 100, // Playing area length (excluding in-goal areas)
  inGoal: 10, // In-goal area depth
  totalLength: 120 // Total length including in-goal areas
};

/**
 * Field zones for analysis
 */
export const FIELD_ZONES = {
  RED_ZONE: { name: 'Red Zone', start: 0, end: 0.27   },    // From try line to 22m (defensive)
  YELLOW_ZONE: { name: 'Yellow Zone', start: 0.27, end: 0.74 }, // Between 22m lines (middle)
  GREEN_ZONE: { name: 'Green Zone', start: 0.74, end: 1 }   // From 22m to try line (attacking)
};

/**
 * Event-specific tags for detailed outcomes
 */
export const EVENT_TAGS = {
  'Lineout': [
    { id: 'won', label: 'Won' },
    { id: 'stolen', label: 'Lost - Stolen' },
    { id: 'lost-overthrow', label: 'Lost - Overthrow' },
    { id: 'lost-not-straight', label: 'Lost - Not Straight' },    
    { id: 'clean-catch', label: 'Clean Catch' },
    { id: 'front-position', label: 'Front Position' },
    { id: 'middle-position', label: 'Middle Position' },
    { id: 'back-position', label: 'Back Position' },
    { id: 'tap-down', label: 'Tap Down' },
    { id: 'deflection', label: 'Deflection' },
    { id: 'early-lift', label: 'Early Lift' }
  ],
  'Scrum': [
    { id: 'won', label: 'Won' },
    { id: 'won-penalty', label: 'Won - Penalty' },
    { id: 'lost', label: 'Lost' },
    { id: 'lost-penalty', label: 'Lost - Penalty' },
    { id: 'lost-free-kick', label: 'Lost - Free Kick' },
    { id: 'wheeled', label: 'Wheeled' },
    { id: 'collapsed', label: 'Collapsed' },
    { id: 'reset', label: 'Reset' },
    { id: 'clean-ball', label: 'Clean Ball' },
    { id: '8-pick-and-go', label: '8 Pick And Go' }
  ],
  'Tackle': [
    { id: 'completed', label: 'Completed' },
    { id: 'missed', label: 'Missed' },
    { id: 'dominant', label: 'Dominant' },
    { id: 'assist', label: 'Assist' },
    { id: 'turnover', label: 'Turnover' },
    { id: 'high', label: 'High' },
    { id: 'late', label: 'Late' }
  ],
  'Kick': [
    { id: 'found-touch', label: 'Found Touch' },
    { id: 'caught-in-field', label: 'Caught In-Field' },
    { id: 'went-dead', label: 'Went Dead' },
    { id: 'charged-down', label: 'Charged Down' },
    { id: 'up-and-under', label: 'Up and Under' },
    { id: 'grubber', label: 'Grubber' },
    { id: 'chip', label: 'Chip' },
    { id: 'box', label: 'Box' },
    { id: '50-22', label: '50-22'}
  ],
  'Penalty': [
    {id: 'cause', label: 'Cause'},
    {id: 'decision', label: 'Decision'},
    { id: 'scrum', label: 'Scrum' },
    { id: 'lineout', label: 'Lineout' },
    { id: 'quick-tap', label: 'Quick Tap' },
    { id: 'kick-at-goal', label: 'Kick at Goal' },
    { id: 'offside', label: 'Offside' },
    { id: 'high-tackle', label: 'High Tackle' },
    { id: 'not-rolling', label: 'Not Rolling Away' },
    { id: 'collapsing', label: 'Collapsing' }
  ],
  'Turnover': [
    { id: 'ruck', label: 'Ruck' },
    { id: 'maul', label: 'Maul' },
    { id: 'interception', label: 'Interception' },
    { id: 'tackle', label: 'Tackle' },
    { id: 'counter-ruck', label: 'Counter Ruck' },
    { id: 'jackal', label: 'Jackal' }
  ],
  'Try': [
    { id: 'corner', label: 'Corner' },
    { id: 'under-posts', label: 'Under Posts' },
    { id: 'forwards', label: 'Forwards' },
    { id: 'backs', label: 'Backs' },
    { id: 'intercept', label: 'Intercept' }
  ],
  'Maul': [
    { id: 'gained-ground', label: 'Gained Ground' },
    { id: 'lost-ground', label: 'Lost Ground' },
    { id: 'turnover', label: 'Turnover' },
    { id: 'collapsed', label: 'Collapsed' },
    { id: 'held-up', label: 'Held Up' }
  ],
  'Ruck': [
    { id: 'quick-ball', label: 'Quick Ball' },
    { id: 'slow-ball', label: 'Slow Ball' },
    { id: 'turnover', label: 'Turnover' },
    { id: 'penalty', label: 'Penalty' },
    { id: 'secured', label: 'Ball Secured'},
    { id: 'ball-lost', label: 'Ball Lost'}
  ],
  'Conversion': [
    { id: 'successful', label: 'Successful' },
    { id: 'missed', label: 'Missed' },
    { id: 'hit-post', label: 'Hit Post' },
    { id: 'charged-down', label: 'Charged Down' }
  ],
  'Penalty Goal': [
    { id: 'successful', label: 'Successful' },
    { id: 'missed', label: 'Missed' },
    { id: 'hit-post', label: 'Hit Post' }
  ],
  'Drop Goal': [
    { id: 'successful', label: 'Successful' },
    { id: 'missed', label: 'Missed' },
    { id: 'hit-post', label: 'Hit Post' },
    { id: 'charged-down', label: 'Charged Down' }
  ]
};

/**
 * Determine which zone a position falls into, accounting for direction change at halftime
 * and the team's initial playing direction
 *
 * @param {number} xPosition - Horizontal position (0-1)
 * @param {string} period - Match period ('First Half', 'Second Half', 'Overtime')
 * @param {string} teamDirection - Team's initial playing direction ('left-to-right' or 'right-to-left')
 * @returns {string} Zone name
 */
export const getFieldZone = (xPosition, period = 'First Half', teamDirection = 'left-to-right') => {
  // Determine if we need to invert based on period and initial direction
  let shouldInvert = false;
  
  if (teamDirection === 'left-to-right') {
    // Default behavior - invert in second half
    shouldInvert = (period === 'Second Half' || period === 'Overtime');
  } else {
    // If starting right-to-left, invert in first half
    shouldInvert = (period === 'First Half');
  }
  
  // Apply inversion if needed
  let adjustedX = shouldInvert ? (1 - xPosition) : xPosition;
  
  if (adjustedX <= FIELD_ZONES.RED_ZONE.end) {
    return FIELD_ZONES.RED_ZONE.name;
  } else if (adjustedX <= FIELD_ZONES.YELLOW_ZONE.end) {
    return FIELD_ZONES.YELLOW_ZONE.name;
  } else {
    return FIELD_ZONES.GREEN_ZONE.name;
  }
};

/**
 * Toast notification durations
 */
export const TOAST_DURATION = {
  short: 2000, // 2 seconds
  normal: 3000, // 3 seconds
  long: 5000 // 5 seconds
};

/**
 * Default match settings
 */
export const DEFAULT_MATCH_SETTINGS = {
  name: 'Rugby Match',
  homeTeam: 'Home',
  awayTeam: 'Away'
};

/**
 * Default filter settings
 */
export const DEFAULT_FILTERS = {
  eventType: '',
  team: '',
  period: ''
};